import { Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useSEO } from '@/hooks/useSEO'
import { getArchives } from '@/lib/api'
import type { Archive } from '@/types'

export default function AboutPage() {
  // SEO
  useSEO({
    title: 'About',
    description: 'A multi-format home for the stories that shaped us — human history across written archives, podcasts, and documentary video.',
    url: 'https://thearchivenetwork.com/about',
  })

  // The Archives list is API-driven (same source as the footer/homepage), so it
  // always reflects the full registry (12 archives today) and never drifts.
  const [archives, setArchives] = useState<Archive[]>([])
  useEffect(() => {
    getArchives().then(setArchives).catch(() => setArchives([]))
  }, [])

  return (
    <div className="animate-fade-in py-16 sm:py-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="font-display text-4xl sm:text-5xl text-archive-cream">
            About The Archive Network
          </h1>
          <p className="mt-4 text-lg text-archive-muted max-w-2xl mx-auto">
            A multi-format home for the stories that shaped us.
          </p>
        </div>

        {/* Mission */}
        <section className="mb-16">
          <h2 className="font-display text-2xl text-archive-cream mb-6">Our Mission</h2>
          <div className="prose prose-invert max-w-none">
            <p className="text-archive-text leading-relaxed">
              History is how we understand ourselves — and how we build what comes next.
              To improve the future, we have to understand what came before it. That
              conviction is why The Archive Network exists.
            </p>
            <p className="text-archive-text leading-relaxed mt-4">
              But there's a problem: fewer and fewer people read. The great histories sit
              unopened; the lessons of the past reach smaller and smaller audiences. So we
              set out to build something different — a place where history isn't locked
              inside dense books, but told in the mediums people actually turn to today.
              Every topic across our archives can be read as a set of clear, structured
              chapters, listened to as podcast episodes, and watched as documentary videos.
              The same knowledge, offered three ways, so that whoever you are and however
              you like to learn, there's a door in for you.
            </p>
            <p className="text-archive-text leading-relaxed mt-4">
              Where an encyclopedia sprawls, we curate. Each subject is a self-contained
              deep-dive — a handful of chapters, a handful of episodes, a handful of films —
              enough to truly understand it, structured so you can actually finish it.
              Twelve thematic archives, from civilizations and conflicts to founders, faiths,
              and the great disasters, each preserving its corner of the human story. And in
              Speak with History, you can go further still — and put your questions directly
              to the figures who lived it.
            </p>
            <p className="text-archive-text leading-relaxed mt-4">
              Our aim is simple, and old as libraries: to preserve knowledge, and to place
              it in front of the largest audience we can reach. History belongs to everyone.
              We're trying to hand it back.
            </p>
          </div>
        </section>

        {/* The Archives */}
        <section className="mb-16">
          <h2 className="font-display text-2xl text-archive-cream mb-6">The Archives</h2>
          <div className="space-y-6">
            {archives.map((archive) => (
              <div key={archive.key} className="artifact-frame p-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div>
                    <h3 className="font-display text-xl text-archive-cream">{archive.name}</h3>
                    <p className="mt-2 text-archive-muted">{archive.description}</p>
                  </div>
                  <div className="flex gap-3 flex-shrink-0">
                    <Link
                      to={`/archive/${archive.key}`}
                      className="text-sm text-archive-gold hover:text-archive-amber"
                    >
                      Gateway
                    </Link>
                    <a
                      href={archive.site_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-archive-gold hover:text-archive-amber"
                    >
                      Visit
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Our Approach */}
        <section className="mb-16">
          <h2 className="font-display text-2xl text-archive-cream mb-6">Our Approach</h2>
          <div className="artifact-frame p-8">
            <div className="prose prose-invert max-w-none">
              <p className="text-archive-text leading-relaxed">
                The Archive Network is an independent project with an ambitious aim: to cover
                the breadth of human history — across written archives, podcasts, and video —
                at a depth and scale usually reserved for whole institutions. We use advanced
                AI as the engine that makes this possible; it's how a single independent
                project can bring so many stories to life in so many forms.
              </p>
              <p className="text-archive-text leading-relaxed mt-4">
                We treat every archive as a living document. We care about getting history
                right, we improve and correct these pages as we learn, and we welcome readers
                who spot something we've missed. Accuracy is a process, not a promise carved
                in stone — and we'd rather be honest about that than pretend otherwise.
              </p>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section>
          <h2 className="font-display text-2xl text-archive-cream mb-6">Contact</h2>
          <div className="artifact-frame p-8 text-center">
            <p className="text-archive-muted mb-6">
              Questions, corrections, or collaboration proposals are always welcome.
            </p>
            <Link
              to="/contact"
              className="btn-primary inline-flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Send a Message
            </Link>
            <p className="mt-4 text-sm text-archive-muted">
              Or email us directly at{' '}
              <a
                href="mailto:contact@thearchivenetwork.com"
                className="text-archive-gold hover:underline"
              >
                contact@thearchivenetwork.com
              </a>
            </p>
          </div>
        </section>
      </div>
    </div>
  )
}
